import { FC, ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import cn from "classnames"; // Keep classnames for flexibility
import { Badge } from "@components/ui/badge";

const buttonVariants = cva(
    `h-fit assented overflow-clip group flex cursor-pointer w-full flex items-center justify-start relative border 
    rounded-2xl transition-all duration-200 ease-in-out text-white 
    focus:outline-none`,
    {
        variants: {
            variant: {
                wallet: "assented bg-muted-foreground/30",
                newWallet: "assented bg-muted-foreground/0 hover:bg-muted-foreground/30",
            },
            selected: {
                true: "",
                false: "",
            },
        },
        defaultVariants: {
            variant: "wallet",
            selected: false,
        },
    },
);

export interface WalletButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    text: string | JSX.Element;
    internal?: boolean;
    newWallet?: boolean | undefined;
    active?: boolean;
    disabled?: boolean;
}

const WalletButton: FC<WalletButtonProps> = ({
    className,
    variant,
    text,
    onClick,
    newWallet,
    internal,
    active,
    disabled,
    ...props
}) => {
    const icon = internal ? "Nest" : "TON";
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={cn(
                newWallet
                    ? buttonVariants({ variant: "newWallet", className })
                    : buttonVariants({ variant, className }),
                { " !bg-muted": active },
                disabled && "!pointer-events-none !opacity-40",
            )}
            {...props}
        >
            <div className="relative w-full h-full flex items-center justify-between z-10 rounded-2xl p-4 py-4 pr-6">
                <div className="flex items-start gap-3 flex-col">
                    <span className="leading-none">{text}</span>
                    {!newWallet &&
                        (internal ? (
                            <div
                                className={cn(
                                    "flex items-center gap-1 -mb-0.5 group-hover:opacity-100 transition-opacity duration-300 ease-in-out",
                                    {
                                        "opacity-70": !active,
                                    },
                                )}
                            >
                                <Badge variant={"external"}>swap</Badge>
                                <Badge variant={"external"}>fast swap</Badge>
                                <Badge variant={"external"}>sniper</Badge>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 -mb-0.5">
                                <Badge variant={"internal"}>swap</Badge>
                            </div>
                        ))}
                </div>
                {!newWallet && <img height={22} width={22} src={`/images/modal/${icon.toLowerCase()}.svg`} alt="" />}
            </div>

            <span
                className={cn(
                    "size-32 absolute -right-10 z-0 rounded-full opacity-20 transition-all translate-x-0 duration-300 ease-in-out blur-lg",
                    internal ? "bg-primary" : "bg-secondary",
                    {
                        "-translate-x-4 opacity-40": active,
                        "group-hover:-translate-x-4 group-hover:opacity-40": !active,
                    },
                )}
            ></span>
        </button>
    );
};

export default WalletButton;
